import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Warning,
  Autorenew,
  Add,
  Star,
  Person,
  Lock,
  Science,
  Shield,
  Archive,
  Edit,
  ExpandMore,
  GroupWork,
  CheckCircleOutline,
} from '@mui/icons-material';
import type { EnvironmentDocument, EnvironmentStatus, Pool } from '@finvari/deliver';
import { isLeaseActive } from '@finvari/deliver';
import { useEnvironments } from '../../hooks/useEnvironments';
import {
  useConfiguredEnvironments,
  useCanEditEnvironment,
} from '../../contexts/EnvironmentsContext';
import { usePools } from '../../contexts/PoolsContext';
import { useConfig } from '../../contexts/ConfigContext';
import EnvironmentFormDialog from '../../components/EnvironmentFormDialog';
import PoolFormDialog from '../../components/PoolFormDialog';
import QueuedDeploysList from '../../components/QueuedDeploysList';
import { useDeploymentStatus } from '../../contexts/DeploymentStatusContext';
import {
  formatDate,
} from '../../utils/deploymentUtils';
import {
  getEnvironmentStatusIcon,
  getEnvironmentStatusColor,
  createEnvironmentNavigationHandlers,
} from '../../utils/environmentUtils';

const Environments: React.FC = () => {
  const navigate = useNavigate();
  // "Show all" reveals archived and others' personal environments in the ungrouped section.
  const [showAll, setShowAll] = React.useState(false);
  // Ungrouped section: visible non-pooled environments (the hook already hides pooled, others'
  // personal, and archived unless includeHidden). Pool sections draw from the full set below.
  const { environments: ungroupedSource, loading, error } = useEnvironments(showAll);
  const { environments: allStatuses } = useEnvironments(true);
  const { userProfile, config } = useConfig();
  const { environmentsById } = useConfiguredEnvironments();
  const { pools, queuedByPoolId } = usePools();
  const canEditEnv = useCanEditEnvironment();
  const canEditConfig = userProfile?.canEditConfig ?? false;

  // The form dialog handles both create (no environment) and edit (an environment doc).
  const [formDialog, setFormDialog] = React.useState<{ open: boolean; environment?: EnvironmentDocument }>({
    open: false,
  });
  const [poolDialog, setPoolDialog] = React.useState<{ open: boolean; pool?: Pool }>({ open: false });
  const {
    getModuleDeploymentsForEnvironment,
    getDeploymentConsistencyInfo
  } = useDeploymentStatus();

  const { handleEnvironmentDetailClick } = createEnvironmentNavigationHandlers(navigate);

  const poolIdOf = (env: EnvironmentStatus): string | undefined => environmentsById[env.environmentId]?.poolId;

  const sortArchivedLast = (envs: EnvironmentStatus[]): EnvironmentStatus[] =>
    [...envs].sort((a, b) => Number(!!a.archived) - Number(!!b.archived));

  // Ungrouped environments split into personal sandboxes and ordinary shared environments. Pooled
  // environments are excluded here (they render under Pools); "Show all" bypasses the hook's
  // visibility filter, so drop pooled explicitly rather than relying on it.
  const ungroupedEnvironments = ungroupedSource.filter((env) => !poolIdOf(env));
  const personalEnvironments = sortArchivedLast(ungroupedEnvironments.filter((env) => !!env.ownerUid));
  const sharedEnvironments = sortArchivedLast(ungroupedEnvironments.filter((env) => !env.ownerUid));

  // Member statuses for a pool, keyed by membership on the raw doc (archived hidden unless "Show all").
  const membersForPool = (pool: Pool): EnvironmentStatus[] =>
    allStatuses
      .filter((env) => poolIdOf(env) === pool.poolId && (showAll || !env.archived))
      .sort((a, b) => a.environmentName.localeCompare(b.environmentName));

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading environments...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Renders a single environment card. Reused for ungrouped environments and pool members.
  const renderEnvironmentCard = (environment: EnvironmentStatus) => {
    const moduleDeployments = getModuleDeploymentsForEnvironment(environment.environmentId);
    const consistencyInfo = getDeploymentConsistencyInfo(moduleDeployments);
    const isDefault = userProfile?.defaultEnvironmentId === environment.environmentId;
    const environmentDoc = environmentsById[environment.environmentId];
    const canEdit = canEditEnv(environment);

    const isPersonal = !!environment.ownerUid;
    const isProtected = config?.protectedEnvironments?.includes(environment.gcpProjectId) ?? false;
    // Guardrail flags come off the raw doc (defaults: canResetData true, canLoadCustomerData false).
    const cannotWipe = environmentDoc?.canResetData === false;
    const demoOnly = !!environmentDoc && environmentDoc.canLoadCustomerData !== true;

    // Lease summary for pooled environments: who holds it and until when, or "available".
    const pooled = !!environmentDoc?.poolId;
    const lease = environmentDoc?.lease;
    const leaseActive = isLeaseActive(lease);
    // An available pooled environment reads as a status of its own — we show just the "Available"
    // chip in place of the operational status, and label its (stale) deployment info accordingly.
    const isAvailablePooled = pooled && !leaseActive;
    const leaseChip = pooled ? (
      leaseActive && lease ? (
        <Tooltip title={`Lease expires ${formatDate(lease.expiresAt)}`}>
          <Chip size="small" color="warning" variant="outlined" label={`Held by ${lease.holderEmail}`} />
        </Tooltip>
      ) : (
        <Chip size="small" color="success" variant="outlined" icon={<CheckCircleOutline />} label="Available" />
      )
    ) : null;

    // Compact status badges shown as labelled icons next to the environment name.
    const badges = [
      isDefault && { key: 'default', label: 'Your default environment', icon: <Star fontSize="small" color="secondary" /> },
      isPersonal && { key: 'personal', label: 'Personal dev environment', icon: <Person fontSize="small" color="secondary" /> },
      isProtected && { key: 'protected', label: 'Protected environment', icon: <Lock fontSize="small" color="secondary" /> },
      demoOnly && { key: 'demo', label: 'Demo data only (no customer snapshot access)', icon: <Science fontSize="small" color="secondary" /> },
      cannotWipe && { key: 'nowipe', label: 'Data wipe disabled', icon: <Shield fontSize="small" color="secondary" /> },
      environment.archived && { key: 'archived', label: 'Archived', icon: <Archive fontSize="small" color="secondary" /> },
    ].filter(Boolean) as Array<{ key: string; label: string; icon: React.ReactNode }>;

    const cardHeaderTitle = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>{environment.environmentName}</span>
        {badges.map((b) => (
          <Tooltip key={b.key} title={b.label}>
            <Box component="span" role="img" aria-label={b.label} sx={{ display: 'inline-flex' }}>
              {b.icon}
            </Box>
          </Tooltip>
        ))}
      </Box>
    );

    const cardHeaderAction = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {leaseChip}
        {!isAvailablePooled && (
          <Chip
            icon={getEnvironmentStatusIcon(environment.status)}
            label={environment.status}
            color={getEnvironmentStatusColor(environment.status)}
            variant="outlined"
          />
        )}
        {canEdit && environmentDoc && (
          <Tooltip title="Edit environment">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setFormDialog({ open: true, environment: environmentDoc });
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

    if (moduleDeployments.size === 0) {
      return (
        <Card key={environment.environmentId}>
          <CardHeader
            title={cardHeaderTitle}
            subheader={environment.gcpProjectId}
            action={cardHeaderAction}
          />
          <CardContent>
            {environment.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {environment.description}
              </Typography>
            )}
            <Alert severity="info">
              No deployments found
            </Alert>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Last updated: {formatDate(environment.lastUpdated)}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const allDeployments = Array.from(moduleDeployments.values());

    // Current-rollout info comes from the persisted activeRollout denorm (written once at activation),
    // not from a per-row rollout lookup. Fall back to the live deployment only for environments that
    // predate the denorm. Its creation-time facts carry no rollout status/finish-time — those are
    // intentionally not shown here (the live operational-status chip above covers current state).
    const active = environment.activeRollout;
    const firstDeployment = allDeployments[0];
    const prNumber = active?.prNumber ?? firstDeployment.prNumber;
    const prTitle = active?.prTitle ?? firstDeployment.prTitle;
    const branch = active?.branch ?? firstDeployment.branch;

    return (
      <Card
        key={environment.environmentId}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 3,
          },
        }}
        onClick={() => handleEnvironmentDetailClick(environment.environmentId)}
      >
        <CardHeader
          title={cardHeaderTitle}
          subheader={environment.gcpProjectId}
          action={cardHeaderAction}
        />
        <CardContent>
          {environment.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {environment.description}
            </Typography>
          )}

          {/* Environment Summary */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
            }}
          >
            <Stack spacing={1}>
              {/* Operational status is shown once, in the card header chip (top right). This area is
                  just the current rollout's identity plus the autodeploy / pool tags. */}
              <Typography variant="subtitle2" fontWeight={600}>
                {isAvailablePooled ? 'Previously deployed' : 'Current rollout'}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                {prNumber ? (
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      PR #{prNumber}
                    </Typography>
                    {prTitle && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {prTitle}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" fontFamily="monospace">
                    {branch}
                  </Typography>
                )}
                {active?.autoDeployOnPush && (
                  <Chip
                    icon={<Autorenew />}
                    label="Autodeploy"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                {allDeployments.length} modules deployed
              </Typography>
            </Stack>
          </Paper>

          {/* Consistency Warning */}
          {!consistencyInfo.isConsistent && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Mixed Deployments
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Not all modules are from the same deployment.
                  Modules <strong>{consistencyInfo.inconsistentModules.join(', ')}</strong> are from different PR/branch.
                </Typography>
              </Stack>
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Last updated: {formatDate(environment.lastUpdated)} • Click to view module details
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 } as const;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Environments
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Real-time environment status and deployment tracking
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => setShowAll((s) => !s)}>
              {showAll ? 'Show fewer' : 'Show all'}
            </Button>
            {canEditConfig && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setPoolDialog({ open: true })}>
                New Pool
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setFormDialog({ open: true })}
            >
              New Environment
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Personal environments */}
      {personalEnvironments.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Personal
          </Typography>
          <Box sx={{ ...gridSx, mb: 4 }}>
            {personalEnvironments.map((environment) => renderEnvironmentCard(environment))}
          </Box>
        </>
      )}

      {/* Shared (ungrouped) environments */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Shared
      </Typography>
      <Box sx={gridSx}>
        {sharedEnvironments.length > 0 ? (
          sharedEnvironments.map((environment) => renderEnvironmentCard(environment))
        ) : (
          <Card sx={{ gridColumn: '1 / -1' }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" align="center" sx={{ py: 4 }}>
                No shared environments.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Pools */}
      {pools.length > 0 && (
        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
          Pools
        </Typography>
      )}
      {pools.map((pool) => {
        const members = membersForPool(pool);
        const queueDepth = (queuedByPoolId[pool.poolId] ?? []).length;
        return (
          <Accordion key={pool.poolId} defaultExpanded sx={{ mt: 3 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap' }}>
                <GroupWork fontSize="small" color="action" />
                <Typography variant="h6">{pool.name}</Typography>
                {pool.description && (
                  <Typography variant="body2" color="text.secondary">
                    {pool.description}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Chip size="small" variant="outlined" label={`Default lease: ${pool.defaultLeaseHours}h`} />
                <Chip
                  size="small"
                  color={queueDepth > 0 ? 'warning' : 'default'}
                  variant="outlined"
                  label={`${queueDepth} queued`}
                />
                {canEditConfig && (
                  <Tooltip title="Edit pool">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPoolDialog({ open: true, pool });
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {members.length > 0 ? (
                <Box sx={gridSx}>{members.map((environment) => renderEnvironmentCard(environment))}</Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No environments in this pool yet.
                </Typography>
              )}
              {queueDepth > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Waiting in queue
                  </Typography>
                  <QueuedDeploysList rollouts={queuedByPoolId[pool.poolId] ?? []} />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <EnvironmentFormDialog
        open={formDialog.open}
        environment={formDialog.environment}
        onClose={() => setFormDialog({ open: false })}
      />
      <PoolFormDialog
        open={poolDialog.open}
        pool={poolDialog.pool}
        onClose={() => setPoolDialog({ open: false })}
      />
    </Container>
  );
};

export default Environments;
